import {HttpClient, HttpHeaders} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {Notification} from '../types/notifications.types';
import {BehaviorSubject, finalize, map, Observable, of, ReplaySubject, switchMap, take, tap} from 'rxjs';
import {UrlEnum} from "../types/url-enum";
import {UrlService} from "./url.service";
import {NotificationStatus} from "../types/notification-status.types";
import {NotificationCategoryEnumTypes} from "../types/notification-category-enum.types";
import {PermissionsService} from "./permissions.service";
import {DateTimeService} from "./date.time.service";
import {UserService} from "./user.service";

@Injectable({ providedIn: 'root' })
export class NotificationsService {
    private _notifications: BehaviorSubject<Notification[]> = new BehaviorSubject<Notification[]>([]);
    private _httpClient = inject(HttpClient);
    private _urlService : UrlService = inject(UrlService);
    private _dateTimeService : DateTimeService = inject(DateTimeService);
    private _permissionService : PermissionsService = inject(PermissionsService);
    private _userService : UserService = inject(UserService);


    /**
     * Constructor
     */
    constructor() {
    }
    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------
    /**
     * Getter for notifications
     */
    get notifications$(): Observable<Notification[]> {
        return this._notifications.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all notifications
     */
    getAll(userId: string): Observable<Notification[]> {
    if(userId == undefined || userId === "") {
        return of([])
    }
        return this._httpClient
            .get<Notification[]>(this._urlService.getUrl(UrlEnum.private_notifications_user_ui).replace('{userId}', userId))
            .pipe(
                map((notifications) =>
                    notifications.map(notification => ({
                        ...notification,
                        icon: this.getIconBasedOnType(notification),
                        time: this._dateTimeService.getDateAndTimeFromBEInDateObject(notification.updatedOn),
                        link: this.getLink(notification.userId)
                    }))
                ),
                tap((notifications) => {
                    this._notifications.next(notifications);
                })
            );
    }

    private getIconBasedOnType(notification: Notification): string {

        switch (notification.notificationCategory) {
            case NotificationCategoryEnumTypes.CALENDAR_EVENT_CREATION:
            case NotificationCategoryEnumTypes.CALENDAR_EVENT_UPDATE:
                return 'event';
            case NotificationCategoryEnumTypes.USER_CREATION:
                return 'feather:user-plus';
            default:
                return 'feather:alert-circle';
        }
    }



    /**
     * Update the notification
     *
     * @param id
     * @param notificationStatus
     */
    update(id: string, notificationStatus: NotificationStatus): Observable<Notification> {
        return this.notifications$.pipe(
            take(1),
            switchMap((notifications) =>
                this._httpClient
                    .put<Notification>(this._urlService.getUrl(UrlEnum.private_notifications).replace('{id}', id),
                        notificationStatus)
                    .pipe(
                        map((updatedNotification: Notification) => {
                            // Find the index of the updated notification
                            const index = notifications.findIndex(
                                (item) => item.id === id
                            );

                            // Update the notification
                            updatedNotification.status = notificationStatus.notificationStatus;
                            notifications[index] = updatedNotification;

                            // Update the notifications
                            this._notifications.next(notifications);

                            // Return the updated notification
                            return updatedNotification;
                        })
                    )
            )
        );
    }

    /**
     * Delete the notification
     *
     * @param id
     * @param notificationStatus
     */
    delete(id: string, notificationStatus: NotificationStatus): Observable<Notification> {
        return this.notifications$.pipe(
            take(1),
            switchMap((notifications) =>
                this._httpClient
                    .put<Notification>(this._urlService.getUrl(UrlEnum.private_notifications).replace('{id}', id),
                        notificationStatus)
                    .pipe(
                        map((updatedNotification: Notification) => {
                            // Find the index of the deleted notification
                            const index = notifications.findIndex(
                                (item) => item.id === id
                            );

                            // Delete the notification
                            notifications.splice(index, 1);

                            // Update the notifications
                            this._notifications.next(notifications);

                            // Return the deleted status
                            return updatedNotification;
                        })
                    )
            )
        );
    }

    /**
     * Mark all notifications as read
     */
    clearAllNotifications():void {
        const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');
        this._httpClient.put<string>(
            this._urlService.getUrl(UrlEnum.private_notifications_clear_all).replace('{userId}', this._userService.getLoggedInUserCurrentValue().id),
            null, {headers: headers}).pipe(tap(result => {

            // Go through all notifications and set them as read
            this._notifications.next([])
        })).subscribe();

    }


    private getLink(userId: string) {
        if(userId){
            return "patients/patient/dental-chart?id="+userId
        }
        return ""
    }
}
